import { IState } from "..";
import { createSlice } from "@reduxjs/toolkit";
import mixpanel from "mixpanel-browser";
import { AppThunk } from "../../app/store";
import PaymentService from "../../app/data/payment/paymentService";
import { PaymentResult } from "../../app/data/payment/PaymentResult";
import { PaymentUtils, assignPaymentStatus, currentReturnTo } from "../../app/data/payment/paymentUtils";
import { reloadInvoiceDetails } from "../invoice/invoiceSlice";
import { reloadShipmentDetails } from "../shipment-details/shipmentDetailsSlice";
import { TokenRequest } from "../../app/data/payment/TokenRequest";
import { TokenInfo } from "../../app/data/payment/TokenInfo";
import { DEFAULT_SURCHARGE_WARNING, initialPaymentState } from "./PaymentState";
import { reloadAgingInvoices } from "../aging/agingInvoicesSlice";

export const PAYMENT_TIMEOUT_KEY_PREFIX = "payment_timeout_";

const paymentService = PaymentService.getInstance();

export const paymentSlice = createSlice({
  name: "payment",
  initialState: initialPaymentState,
  reducers: {
    resetPayment: (state) => ({
      ...initialPaymentState,
      blockedInvoices: state.blockedInvoices,
      surchargeWarning: state.surchargeWarning,
      surchargeEnabled: state.surchargeEnabled,
      achBankDetails: state.achBankDetails,
      availableMethods: state.availableMethods,
    }),
    paymentLoadingStarted: (state, { payload }) => {
      state.loading = true;
      state.showPopup = true;
      state.showMethodPicker = false;
      state.loadingFailed = false;
      state.loaded = false;
      state.error = null;
      if (payload && payload !== 0) {
        state.paymentSum = payload;
      }
    },
    paymentLoadingSucceed: (state, { payload }) => {
      state.loading = false;
      state.loaded = true;
      state.tokenInfo = payload;
    },
    paymentLoadingFailed: (state, { payload }) => {
      state.loading = false;
      state.loadingFailed = true;
      state.tokenInfo = null;
      state.error = payload;
    },
    setPaymentInfo: (state, { payload }) => {
      PaymentUtils.setIsSuccess(payload);
      state.paymentResult = payload;
    },
    setPaymentInitiator: (state, { payload }) => {
      state.paymentInitiator = payload;
    },
    setPaymentHiddenFormSubmitted: (state) => {
      state.hiddenFormSubmitted = true;
    },
    setPaymentTimedOut: (state) => {
      state.showPopup = false;
      state.hiddenFormSubmitted = false;
      state.tokenInfo = null;
      state.timedOut = true;
    },
    setInvoicePaymentBlocked: (state, { payload }: { payload: string }) => {
      state.blockedInvoices[payload] = true;
      if (payload.length > 1) {
        localStorage.setItem(
          `${PAYMENT_TIMEOUT_KEY_PREFIX}${payload}`,
          JSON.stringify({ timedOut: true })
        );
      }
    },
    clearInvoicePaymentBlocked: (state, { payload }: { payload: string }) => {
      delete state.blockedInvoices[payload];
      localStorage.removeItem(`${PAYMENT_TIMEOUT_KEY_PREFIX}${payload}`);
    },
    rehydrateBlockedInvoices: (state) => {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(PAYMENT_TIMEOUT_KEY_PREFIX)) {
          const invoiceId = key.replace(PAYMENT_TIMEOUT_KEY_PREFIX, "");
          const item = localStorage.getItem(key);
          if (item && JSON.parse(item).timedOut) {
            state.blockedInvoices[invoiceId] = true;
          }
        }
      });
    },
    openMethodPicker: (state, { payload }) => {
      state.showMethodPicker = true;
      state.showPopup = false;
      state.error = null;
      state.pendingInvoiceNumbers = payload.invoiceNumbers || [];
      if (payload.paymentSum != null) {
        state.paymentSum = payload.paymentSum;
      }
      if (payload.initiator) {
        state.paymentInitiator = payload.initiator;
      }
    },
    closeMethodPicker: (state) => {
      state.showMethodPicker = false;
    },
    setSurchargeWarning: (state, { payload }) => {
      state.surchargeWarning = payload || DEFAULT_SURCHARGE_WARNING;
    },
    setSurchargeEnabled: (state, { payload }) => {
      state.surchargeEnabled = payload !== false;
    },
    setAchBankDetails: (state, { payload }) => {
      state.achBankDetails = payload || initialPaymentState.achBankDetails;
    },
    setAvailableMethods: (state, { payload }) => {
      state.availableMethods = payload || [];
    },
    setSessionPaymentId: (state, { payload }) => {
      state.sessionPaymentId = payload;
    },
    setConfirmStatus: (state, { payload }) => {
      state.confirmStatus = payload;
      state.loading = false;
    },
    confirmLoadingStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
  },
});

export const {
  resetPayment,
  paymentLoadingStarted,
  paymentLoadingSucceed,
  paymentLoadingFailed,
  setPaymentInfo,
  setPaymentInitiator,
  setPaymentHiddenFormSubmitted,
  setPaymentTimedOut,
  setInvoicePaymentBlocked,
  clearInvoicePaymentBlocked,
  openMethodPicker,
  closeMethodPicker,
  setSurchargeWarning,
  setSurchargeEnabled,
  setAchBankDetails,
  setAvailableMethods,
  setSessionPaymentId,
  setConfirmStatus,
  confirmLoadingStarted,
} = paymentSlice.actions;

export const paymentSelector = (state: IState) => state.payment;

export const selectIsInvoicePaymentBlocked =
  (invoiceId: string) =>
  (state: IState): boolean =>
    state.payment.blockedInvoices[invoiceId] ?? false;

export const isInvoicePaymentAlreadyBlocked = (invoiceId: string): boolean => {
  const raw = localStorage.getItem(`${PAYMENT_TIMEOUT_KEY_PREFIX}${invoiceId}`);
  let isBlocked = raw ? JSON.parse(raw || "{}").timedOut : false;
  return isBlocked;
};

export const startInvoicePaymentTimeout =
  (invoiceId: string): AppThunk =>
  (dispatch) => {
    dispatch(setInvoicePaymentBlocked(invoiceId));
  };

/** Opens method picker; does not call payment providers until user chooses a method. */
export const startPaymentFlow = (args: {
  invoiceNumbers: number[];
  paymentSum?: number;
  initiator?: string;
  pendingPaymentId?: string;
}): AppThunk => async (dispatch) => {
  if (args.pendingPaymentId) {
    assignPaymentStatus(args.pendingPaymentId);
    return;
  }
  if (!args.invoiceNumbers?.length) {
    return;
  }
  dispatch(
    openMethodPicker({
      invoiceNumbers: args.invoiceNumbers,
      paymentSum: args.paymentSum,
      initiator: args.initiator || (args.invoiceNumbers.length > 1 ? "list" : "details"),
    })
  );
  const methodsResponse = await paymentService.getAvailablePaymentMethods(args.invoiceNumbers);
  if (methodsResponse.ok() && methodsResponse.data) {
    const methods = methodsResponse.data.methods || [];
    dispatch(setAvailableMethods(methods));
    const cc = methods.find((m) => m.id === "CREDIT_CARD");
    if (cc) {
      dispatch(setSurchargeEnabled(cc.surchargeEnabled !== false));
      if (cc.surchargeWarning) {
        dispatch(setSurchargeWarning(cc.surchargeWarning));
      }
    }
    if (methodsResponse.data.achBankDetails) {
      dispatch(setAchBankDetails(methodsResponse.data.achBankDetails));
    }
  }
};

export const selectPaymentMethod = (
  method: "CREDIT_CARD" | "ECHECK" | "BANK"
): AppThunk => async (dispatch, getState) => {
  const state = getState().payment;
  const invoiceNumbers = state.pendingInvoiceNumbers;
  if (!invoiceNumbers.length) {
    return;
  }
  dispatch(closeMethodPicker());
  dispatch(paymentLoadingStarted(state.paymentSum));

  const apiMethod = method === "BANK" ? "ECHECK" : method;
  const response = await paymentService.createSession({
    invoiceNumbers,
    paymentMethod: apiMethod,
    returnTo: currentReturnTo(),
  });

  if (!response.ok()) {
    const err = response.getError?.() || "Failed to start payment";
    if (typeof err === "string" && err.includes("Payment already in progress:")) {
      const parts = err.split(":");
      const paymentId = parts[1];
      if (paymentId) {
        assignPaymentStatus(paymentId);
        return;
      }
    }
    dispatch(paymentLoadingFailed(err));
    return;
  }

  const data = response.data;
  dispatch(setSessionPaymentId(data.paymentId));

  if (data.redirectUrl) {
    window.location.assign(data.redirectUrl);
    return;
  }

  if (data.authorizeNetToken) {
    const tokenInfo = data.authorizeNetToken as TokenInfo;
    if (!isAuthorizeNetTokenSuccess(tokenInfo)) {
      dispatch(paymentLoadingFailed(getAuthorizeNetTokenError(tokenInfo)));
      return;
    }
    dispatch(paymentLoadingSucceed(tokenInfo));
    return;
  }

  dispatch(paymentLoadingFailed("Unexpected payment session response"));
};

const isAuthorizeNetTokenSuccess = (token: TokenInfo | null | undefined): boolean => {
  if (!token?.token) {
    return false;
  }
  const resultCode = token.messages?.resultCode;
  return resultCode === "Ok" || resultCode === "OK";
};

const getAuthorizeNetTokenError = (token: TokenInfo | null | undefined): string => {
  const messages = token?.messages?.message;
  if (Array.isArray(messages) && messages.length) {
    const text = messages
      .map((m) => m?.text)
      .filter(Boolean)
      .join("; ");
    if (text) {
      return text;
    }
  }
  return "Authorize.Net payment form failed";
};

/** @deprecated Prefer startPaymentFlow */
export const getPaymentInfo = (
  invoiceNumber: number,
  _subAccountId?: string
): AppThunk => async (dispatch) => {
  dispatch(
    startPaymentFlow({
      invoiceNumbers: [invoiceNumber],
      initiator: "details",
    })
  );
};

/** @deprecated Prefer startPaymentFlow */
export const getMultiplePaymentInfo = (
  request: TokenRequest,
  paymentSum?: number
): AppThunk => async (dispatch) => {
  const invoiceNumbers = request.invoiceNumbers?.length
    ? request.invoiceNumbers
    : request.invoiceNumber
    ? [request.invoiceNumber]
    : [];
  dispatch(
    startPaymentFlow({
      invoiceNumbers,
      paymentSum,
      initiator: "list",
    })
  );
};

export const confirmPaymentReturn = (
  paymentId: string,
  providerReturnStatus?: string | null
): AppThunk => async (dispatch) => {
  dispatch(confirmLoadingStarted());
  const confirmResponse = await paymentService.confirmPayment(
    paymentId,
    providerReturnStatus
  );
  if (confirmResponse.ok()) {
    dispatch(setConfirmStatus(confirmResponse.data));
    if (confirmResponse.data.status === "COMPLETED") {
      dispatch(reloadInvoiceDetails());
      dispatch(reloadShipmentDetails());
      dispatch(reloadAgingInvoices());
      mixpanel.track("Paid an invoice", {
        Invoice: (confirmResponse.data.invoiceNumbers || []).join(","),
        Amount: confirmResponse.data.amountCents,
        Provider: confirmResponse.data.provider,
      });
    }
    return confirmResponse.data;
  }
  const statusResponse = await paymentService.getPaymentStatus(paymentId);
  if (statusResponse.ok()) {
    dispatch(setConfirmStatus(statusResponse.data));
    return statusResponse.data;
  }
  dispatch(paymentLoadingFailed(confirmResponse.getError?.() || "Confirm failed"));
  return null;
};

export const savePayment = (requestModel: PaymentResult): AppThunk => async (
  dispatch,
  getState
) => {
  let retryLimit = 10;
  dispatch(setPaymentInfo(requestModel));
  mixpanel.track("Paid an invoice", {
    Invoice: requestModel.orderDescription,
    Amount: requestModel.totalAmount,
  });
  while (retryLimit > 0) {
    const response = await paymentService.savePayment(requestModel);
    if (response.ok()) {
      if (requestModel.isSuccess) {
        dispatch(reloadInvoiceDetails());
        dispatch(reloadShipmentDetails());
        dispatch(reloadAgingInvoices());
      }
      const paymentId = getState().payment.sessionPaymentId;
      if (paymentId) {
        assignPaymentStatus(paymentId, currentReturnTo());
      }
      break;
    }
    console.error("Couldn't save. Trying again after 3 seconds.");
    await new Promise((r) => setTimeout(r, 3000));
    retryLimit--;
  }
};

const paymentReducer = paymentSlice.reducer;
export default paymentReducer;
